
import { EBenefitTypes, EShowMode } from "../enums/benefits.enums";
import { CompanyBenefitDto, Entitlement, GuardDecision } from "../types/benefits.types";

function safeNum(v: any, fallback = 0) {
    return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function normalizeBenefitType(type: unknown): EBenefitTypes {
    const raw = typeof type === "string" ? type.trim() : "";
    const normalized = raw.toLowerCase();

    const match = Object.values(EBenefitTypes).find((t) => t.toLowerCase() === normalized);
    if (match) return match;

    // Fallback: keep the original value if it's already a valid enum member
    if ((Object.values(EBenefitTypes) as string[]).includes(raw)) {
        return raw as EBenefitTypes;
    }

    // If it's not a known type, still return it as EBenefitTypes to avoid breaking runtime.
    return raw as EBenefitTypes;
}

export function computeEntitlement(dto: CompanyBenefitDto, now = new Date()): Entitlement {
    const cfg = dto.benefit.config ?? {};
    const isLimitedUsage = !!cfg.isLimitedUsage;
    const limit = isLimitedUsage ? safeNum(cfg.limitedUsage, 0) : undefined;

    const used = safeNum(dto.usageCount, 0);

    const expDate = dto.expDate;
    const isExpired = expDate ? new Date(expDate).getTime() <= now.getTime() : false;

    const remaining =
        isLimitedUsage && typeof limit === "number" ? Math.max(0, limit - used) : undefined;

    const canUseNow =
        !isExpired &&
        (!isLimitedUsage || (typeof limit === "number" ? used < limit : true));

    return {
        type: normalizeBenefitType(dto.benefit.type),
        name: dto.benefit.name,
        enabledByPlan: true,
        isLimitedUsage,
        limit,
        used,
        remaining,
        expDate,
        isExpired,
        canUseNow,
    };
}

const decisionCache = new WeakMap<object, Map<EShowMode, GuardDecision>>();
const undefinedDecisionCache = new Map<EShowMode, GuardDecision>();

export function computeDecision(
    ent: Entitlement | undefined,
    showWhenBlocked: EShowMode
): GuardDecision {
    if (!ent) {
        let cached = undefinedDecisionCache.get(showWhenBlocked);
        if (!cached) {
            cached = { mode: showWhenBlocked, reason: "MISSING" };
            undefinedDecisionCache.set(showWhenBlocked, cached);
        }
        return cached;
    }

    let entCache = decisionCache.get(ent);
    if (!entCache) {
        entCache = new Map<EShowMode, GuardDecision>();
        decisionCache.set(ent, entCache);
    }

    let cached = entCache.get(showWhenBlocked);
    if (cached) {
        return cached;
    }

    let decision: GuardDecision;
    if (ent.isExpired) {
        decision = {
            mode: showWhenBlocked,
            reason: "EXPIRED",
            used: ent.used,
            limit: ent.limit,
            remaining: ent.remaining,
            expDate: ent.expDate,
        };
    } else if (ent.isLimitedUsage && typeof ent.limit === "number" && ent.used >= ent.limit) {
        decision = {
            mode: showWhenBlocked,
            reason: "LIMIT_REACHED",
            used: ent.used,
            limit: ent.limit,
            remaining: ent.remaining,
            expDate: ent.expDate,
        };
    } else {
        decision = {
            mode: EShowMode.ENABLED,
            used: ent.used,
            limit: ent.limit,
            remaining: ent.remaining,
            expDate: ent.expDate,
        };
    }

    entCache.set(showWhenBlocked, decision);
    return decision;
}
